import { i18n } from '@I18n/index'
import { user, popIn, notif, office } from '@Store/modules'
import { IPopin, IOffice } from '@Store/types'
import { UserRfeStatus } from '@Enums/index'

export class RfeHelper {

	// le compte principal dispose déjà d'un pack RFE actif
	static hasActiveRfe(): boolean {
		const current = user.currentUser
		return !!current && !!current.rfe && current.rfe.status === UserRfeStatus.Accepted
	}

	// Le remplaçant n'a pas encore de pack RFE.
	static substituteHasNoRfePack(substituteId: string): boolean {
		const current = user.currentUser
		if (!current || !current.substitutes) return false
		return current.substitutes.some(office =>
			(office.substitutes || []).some(sub =>
				sub.id === substituteId && !(sub.rfe && sub.rfe.status === UserRfeStatus.Accepted)))
	}

	// ici je verifie si je peux activer un pack RFE pour ce remplaçant déjà rattaché ?
	static canProposeSubstitutePack(substituteId: string): boolean {
		return RfeHelper.hasActiveRfe() && RfeHelper.substituteHasNoRfePack(substituteId)
	}

	// ici j'ouvre le popin proposant de souscrire un pack RFE pour un remplaçant.
	static proposeSubstitutePack(substituteId: string, substituteName: string): void {
		let popin: IPopin = {
			title: i18n.t('vm.components.popin.substitute-rfe.title').toString(),
			content: { component: 'popin-substitute-rfe' },
			isWide: true,
			options: {
				substituteId: substituteId,
				substituteName: substituteName
			},
			action: {
				label: i18n.t('vm.components.popin.substitute-rfe.confirm').toString(),
				title: i18n.t('vm.components.popin.substitute-rfe.confirm').toString(),
				callback: async (options: { substituteId: string }, updateLoading: (loading: boolean) => void) => {
					try {
						updateLoading(true)
						await user.updateRfeChoice({ choice: '1', substituteIds: [options.substituteId] })
						notif.success({ message: i18n.t('vm.components.popin.substitute-rfe.success').toString(), actions: [], delay: 5000, canClose: true })
					} catch (err) {
						notif.error({ message: i18n.t('vm.components.popin.substitute-rfe.failed').toString(), actions: [], delay: 5000, canClose: true })
					} finally {
						await user.reloadUser()
						updateLoading(false)
					}
				},
				stayOpen: false
			},
			cancelLabel: i18n.t('vm.components.popin.substitute-rfe.later').toString()
		}

		popIn.info(popin)
	}

	// le titulaire remplit toutes les conditions pour choisir un cabinet de réception RFE :
	// licence valide, pack RFE accepté, numéro PA renseigné
	static canChooseDefaultOffice(): boolean {
		if (!user.isMainUser) return false

		const current = user.currentUser
		if (!current || !current.rfe) return false

		const licenseValid = !current.isSubscriptionRequired
		const paAccepted = current.rfe.status === UserRfeStatus.Accepted
		const paCode = !!current.rfe.pa_code

		return licenseValid && paAccepted && paCode
	}

	private static toBool(value: any): boolean {
		return String(value) === 'true'
	}

	// ici je verifie si aucun cabinet n'a encore été désigné comme cabinet de réception des factures RFE
	static hasNoDefaultOffice(): boolean {
		return !office.offices.some(o => RfeHelper.toBool(o.default_invoice_reception))
	}

	// les cabinets actifs rattachés à l'utilisateur .
	static activeOffices(): IOffice[] {
		return office.offices.filter(o => office.isActive(o))
	}

	// le popin de choix du cabinet est proposé s'il ya au moins deux cabinet
	static shouldChooseDefaultOffice(): boolean {
		return RfeHelper.activeOffices().length > 1
			&& RfeHelper.canChooseDefaultOffice()
			&& RfeHelper.hasNoDefaultOffice()
	}

	// mêmes conditions d'éligibilité que le popin (titulaire, licence valide, pack RFE accepté,
	// code PA et au moins deux cabinets), mais sans la contrainte « aucun cabinet par
	// défaut » : ici l'utilisateur peut revenir sur son choix à tout moment depuis la page Cabinets.
	static canManageDefaultOffice(): boolean {
		return RfeHelper.activeOffices().length > 1
			&& RfeHelper.canChooseDefaultOffice()
	}

	// le cabinet actuellement désigné comme cabinet de réception des factures RFE.
	static defaultOfficeId(): string {
		const current = RfeHelper.activeOffices().find(o => RfeHelper.toBool(o.default_invoice_reception))
		return current ? current.id : ''
	}

	// ici j'ouvre le popin permettant de choisir le cabinet qui recevra les factures RFE.
	static chooseDefaultOffice(): void {
		let popin: IPopin = {
			title: i18n.t('vm.components.popin.default-office-rfe.title').toString(),
			content: { component: 'popin-default-office-rfe' },
			isWide: true,
			options: {
				ofiId: ''
			},
			action: {
				label: i18n.t('vm.components.popin.default-office-rfe.confirm').toString(),
				title: i18n.t('vm.components.popin.default-office-rfe.confirm').toString(),
				condition: (options: { ofiId: string }) => !!options && !!options.ofiId,
				callback: async (options: { ofiId: string }, updateLoading: (loading: boolean) => void) => {
					try {
						updateLoading(true)
						await user.updateRfeDefaultOffice({ ofiId: options.ofiId })
						notif.success({ message: i18n.t('vm.components.popin.default-office-rfe.success').toString(), actions: [], delay: 5000, canClose: true })
					} catch (err) {
						notif.error({ message: i18n.t('vm.components.popin.default-office-rfe.failed').toString(), actions: [], delay: 5000, canClose: true })
					} finally {
						await user.reloadUser()
						updateLoading(false)
					}
				},
				stayOpen: false
			},
			cancelLabel: i18n.t('vm.components.popin.default-office-rfe.later').toString()
		}

		popIn.info(popin)
	}
}
